<template>
  <div v-if="authStore.user" class="flex items-center">
    <p class="text-[#FFFFFF] font-semibold text-[16px] mr-4">Team Members</p>
    <ul class="flex -space-x-2">
      <li v-for="(member, index) in teamMembers" :key="member.id">
        <div class="h-[40px] w-[40px] rounded-full relative" :style="{ zIndex: 1000 - index }">
          <img v-if="member.logo_url" class="h-full w-full rounded-full object-cover" :src="member.logo_url" :alt="member.name" />
          <div v-else class="h-full w-full border-[1px] rounded-full flex items-center justify-center" :style="{ backgroundColor: '#EDEFEE' }">
            <span class="text-[#414141] font-['Open_Sans'] font-semibold text-[16px] leading-[150%] tracking-[0%]">{{ getInitials(member) }}</span>
          </div>
        </div>
      </li>
      <!-- Plus icon to open popup -->
      <li>
        <button @click="toggleInvitation" style="z-index: 0">
          <img class="h-[40px] w-[40px] rounded-full" src="@/assets/u4.svg" alt="Add Member" />
        </button>
      </li>
    </ul>

    <!-- Popup Modal using the reference styling -->
    <div
      class="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto bg-black/20 "
      v-if="isInvitationOpen"
      @click="handleOutsideClick"
    >
      <div
        class="absolute top-[92px] right-0 w-full md:w-1/3 h-[calc(100vh-108px)] min-h-[1px] bg-white p-4 md:p-6 md:pt-10 rounded-[12px] border border-[#00423D] shadow-[0px_0px_9.3px_0px_#00000040] overflow-y-auto"
        ref="invitationWindow"
      >
        <div
          class="flex items-center justify-between border-b border-gray-300 pb-2"
        >
          <h3 class="font-minion font-semibold text-primary-400 text-2xl">
            Invite People To {{ organizationName }}
          </h3>

          <button class="" @click="toggleInvitation">
            <svg
              class="w-7 h-7"
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <mask
                id="mask0_1304_29357"
                style="mask-type: alpha"
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="48"
                height="48"
              >
                <rect width="48" height="48" fill="#D9D9D9" />
              </mask>
              <g mask="url(#mask0_1304_29357)">
                <path
                  d="M12.4501 37.6496L10.3501 35.5496L21.9001 23.9996L10.3501 12.4496L12.4501 10.3496L24.0001 21.8996L35.5501 10.3496L37.6501 12.4496L26.1001 23.9996L37.6501 35.5496L35.5501 37.6496L24.0001 26.0996L12.4501 37.6496Z"
                  class="fill-placeholder"
                />
              </g>
            </svg>
          </button>
        </div>
        
        <div class="mt-5">
          <form
            class="mt-5 flex-1 flex flex-col"
            @submit.prevent="onFormSubmit"
          >
            <!-- Email input section using EmailTagsInput component -->
            <div
              class="flex-1"
              :class="`${showEmailError ? 'shakeX' : ''}`"
            >
              <!-- Using the EmailTagsInput component -->
              <EmailTagsInput 
                v-model="form.emails" 
                label="Add Members via Email" 
                required 
                :errorMessage="showEmailError ? 'At least one Email is required!' : ''"
                @input="resetError"
                @keydown.enter="onFormSubmit"
                @focus="isInputFocused = true"
                @blur="isInputFocused = false"
              />

              <!-- Success message for sent invitations -->
              <transition name="auth-form">
                <p
                  class="pl-2 mt-1 text-sm text-center invite-message"
                  v-if="inviteMessage"
                >
                  {{ inviteMessage }}
                </p>
              </transition>
              <p class="text-sm text-placeholder mt-2 md:text-base md:mt-4">
                Your teammates will get an email that gives them access to your
                team. Press Enter or click Invite to send.
              </p>
            </div>

            <!-- Form submit button -->
            <button
              class="w-full bg-[#00423D] font-minion text-[20px] text-white h-12 md:h-14 rounded-[16px] mt-4 font-bold lg:py-4 flex items-center justify-center"
              type="submit"
              ref="submitBtn"
            >
              <transition name="button-switch" mode="out-in">
                <span v-if="!isSendingRequest">Invite</span>
                <div
                  v-else
                  class="max-h-6 overflow-hidden inline-flex items-center justify-center pt-3"
                >
                  <Vue3Lottie
                    :animationData="buttonAnimation"
                    :height="120"
                    :width="120"
                  />
                </div>
              </transition>
            </button>
          </form>

          <!-- All Members Section -->
          <div class="mt-3">
            <p class="font-minion font-semibold text-primary-400">All Members</p>
            <div class="mt-4">
              <div v-for="(member, idx) in teamMembers" :key="idx" class="flex items-center mt-3 px-2 py-2 rounded-md border border-primary-300">
                <div class="flex items-center gap-2 flex-1">
                  <div class="h-[40px] w-[40px] min-w-[40px] rounded-full relative flex-shrink-0">
                    <img v-if="member.logo_url" class="h-full w-full rounded-full object-cover" :src="member.logo_url" :alt="member.name" />
                    <div v-else class="h-full w-full border-[1px] rounded-full flex items-center justify-center" :style="{ backgroundColor: '#EDEFEE' }">
                      <span class="text-[#414141] font-['Open_Sans'] font-semibold text-[16px] leading-[150%] tracking-[0%]">{{ getInitials(member) }}</span>
                    </div>
                  </div>
                  <div class="flex w-full flex-wrap justify-between pr-1">
                    <div class="w-56 overflow-hidden">
                      <p class="font-minion font-semibold text-primary-300">
                        {{ member.name || member.email }}
                      </p>
                      <p class="font-minion font-semibold text-primary-400 text-sm">
                        {{ member.role || "Member" }}
                      </p>
                    </div>
                    <div
                      v-if="!member.invite_accepted"
                      class="pendingActions flex items-center space-x-2"
                    >
                      <p class="font-minion font-semibold text-primary-300">Pending</p>
                      <button 
                        @click="handleResendInvite(member.email)"
                        class="hover:bg-gray-100 p-1 rounded-full"
                      >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                      <path
                        d="M12 18H5C4.46957 18 3.96086 17.7893 3.58579 17.4142C3.21071 17.0391 3 16.5304 3 16V6C3 5.46957 3.21071 4.96086 3.58579 4.58579C3.96086 4.21071 4.46957 4 5 4H19C19.5304 4 20.0391 4.21071 20.4142 4.58579C20.7893 4.96086 21 5.46957 21 6V13.5"
                        stroke="black"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M3 6L12 12L21 6M15 18H21M21 18L18 15M21 18L18 21"
                        stroke="black"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  v-if="currentUserId !== member.id && member.role !== 'owner'"
                  class="h-10 w-8 flex items-center justify-center"
                  @click="removeMember(member.id)"
                >
                  <svg
                    class="w-7 h-7"
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <mask
                      id="mask0_1304_29357"
                      style="mask-type: alpha"
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="48"
                      height="48"
                    >
                      <rect width="48" height="48" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_1304_29357)">
                      <path
                        fill="#FF0000"
                        d="M12.4501 37.6496L10.3501 35.5496L21.9001 23.9996L10.3501 12.4496L12.4501 10.3496L24.0001 21.8996L35.5501 10.3496L37.6501 12.4496L26.1001 23.9996L37.6501 35.5496L35.5501 37.6496L24.0001 26.0996L12.4501 37.6496Z"
                      />
                    </g>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="flex items-center">
    <p class="text-[#FFFFFF] font-semibold text-[16px]">Team Members</p>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import useVuelidate from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import EmailTagsInput from "@/components/EmailTagsInput.vue";
import { Vue3Lottie } from "vue3-lottie";
import loadingAnimation from "@/assets/loading-button-simple.json";
import Swal from "sweetalert2";
import global from "@/global";
import { useAuthStore } from "@/stores/auth";

// Props
const props = defineProps({
  organizationName: {
    type: String,
    default: "Your Team"
  }
});

// Stores
const authStore = useAuthStore();

// State
const teamMembers = ref([]);
const isInvitationOpen = ref(false);
const currentUserId = ref(null);
const form = ref({
  emails: [],
});
const showEmailError = ref(false);
const isInputFocused = ref(false);
const inviteMessage = ref("");
const isSendingRequest = ref(false);
const buttonAnimation = ref(loadingAnimation);
const invitationWindow = ref(null);

// Validation
const rules = computed(() => ({
  emails: {
    required: helpers.withMessage("At least one email is required!", (value) => value.length > 0),
  },
}));

const v$ = useVuelidate(rules, form);

// Lifecycle
onMounted(() => {
  if (authStore.user) {
    fetchTeamMembers();
    getCurrentUser();
  }
});

// Watch for auth state changes
watch(() => authStore.user, (newUser) => {
  if (newUser) {
    fetchTeamMembers();
    getCurrentUser();
  }
});

// API Methods
const fetchTeamMembers = async () => {
  if (!authStore.user || !authStore.user.accessToken) {
    console.log("Cannot fetch team members: No authenticated user");
    return;
  }

  try {
    const res = await fetch(`${global.API_URL}/users/team-members`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
    });
    const data = await res.json();
    teamMembers.value = data.data.map(member => ({
      id: member.id,
      name: member.first_name && member.last_name 
        ? `${member.first_name} ${member.last_name}` 
        : member.email,
      email: member.email,
      logo_url: member.logo_url,
      role: member.role || "Member",
      invite_accepted: member.invite_accepted,
      first_name: member.first_name,
      last_name: member.last_name
    }));
  } catch (err) {
    console.error("Failed to fetch team members:", err);
    if (authStore.user) {
      Swal.fire("Error", "Failed to fetch team members", "error");
    }
  }
};

const getCurrentUser = () => {
  if (!authStore.user) {
    console.log("Cannot get current user: No authenticated user");
    return;
  }
  currentUserId.value = authStore.user.id;
};

const toggleInvitation = () => {
  isInvitationOpen.value = !isInvitationOpen.value;
  if (isInvitationOpen.value && authStore.user) {
    fetchTeamMembers();
  }
};

const handleOutsideClick = (event) => {
  if (invitationWindow.value && !invitationWindow.value.contains(event.target)) {
    toggleInvitation();
  } else if (!isInputFocused.value) {
    showEmailError.value = false;
  }
};

const onFormSubmit = async () => {
  v$.value.$touch();

  if (form.value.emails.length === 0) {
    showEmailError.value = true;
    return;
  }
  
  if (!authStore.user || !authStore.user.accessToken) {
    console.error("Cannot send invitation: No authenticated user");
    inviteMessage.value = "Authentication required";
    setTimeout(() => {
      inviteMessage.value = "";
    }, 5000);
    return;
  }
  
  try {
    isSendingRequest.value = true;
    
    const res = await fetch(`${global.API_URL}/users/invite`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ emails: form.value.emails }),
    });

    const data = await res.json();
    
    // Always refresh team members list
    await fetchTeamMembers();
    
    // If there's data in the response (like existing emails), show that message as error
    if (data.data) {
      inviteMessage.value = data.data;
      // Add error class to show message in red
      const messageElement = document.querySelector('.invite-message');
      if (messageElement) {
        messageElement.classList.add('text-red-500');
      }
    } else if (data.status === true) {
      inviteMessage.value = "Invitation Sent";
      form.value.emails = [];
      showEmailError.value = false;
      // Remove error class for success message
      const messageElement = document.querySelector('.invite-message');
      if (messageElement) {
        messageElement.classList.remove('text-red-500');
      }
    } else {
      inviteMessage.value = "Failed to send invitation";
      // Add error class for failure message
      const messageElement = document.querySelector('.invite-message');
      if (messageElement) {
        messageElement.classList.add('text-red-500');
      }
    }

    // Clear the message after 3 seconds
    setTimeout(() => {
      inviteMessage.value = "";
      // Remove error class when clearing message
      const messageElement = document.querySelector('.invite-message');
      if (messageElement) {
        messageElement.classList.remove('text-red-500');
      }
    }, 3000);

  } catch (err) {
    console.error("Invitation error:", err);
    inviteMessage.value = "Failed to send invitation. Please try again.";
    // Add error class for error message
    const messageElement = document.querySelector('.invite-message');
    if (messageElement) {
      messageElement.classList.add('text-red-500');
    }
    setTimeout(() => {
      inviteMessage.value = "";
      // Remove error class when clearing message
      if (messageElement) {
        messageElement.classList.remove('text-red-500');
      }
    }, 5000);
  } finally {
    isSendingRequest.value = false;
  }
};

const handleResendInvite = async (email) => {
  if (!authStore.user || !authStore.user.accessToken) {
    console.error("Cannot resend invitation: No authenticated user");
    Swal.fire("Error", "Authentication required", "error");
    return;
  }

  try {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to invite again!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#0D423D",
      confirmButtonText: "Yes, send invite!",
    });

    if (result.isConfirmed) {
      const res = await fetch(`${global.API_URL}/users/resend_invite_email`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });
            
      // Clone the response and log the text content for debugging
      const responseClone = res.clone();
      const textResponse = await responseClone.text();
      
      // Try to parse JSON response
      let data;
      try {
        data = JSON.parse(textResponse);
      } catch (parseError) {
        console.error("Error parsing JSON resend response:", parseError);
        // Check if request was successful by status code
        if (res.ok) {
          Swal.fire("Success!", "Invite resent successfully.", "success");
          fetchTeamMembers(); // Refresh to update status
          return;
        } else {
          throw new Error("Invalid response format from server");
        }
      }
      
      // Check for success using either 'status' or 'success' property
      if (data.success === true || data.status === true) {
        Swal.fire("Success!", "Invite resent successfully.", "success");
        fetchTeamMembers(); // Refresh to update status
      } else {
        Swal.fire("Error", data.message || "Failed to resend invite", "error");
      }
    }
  } catch (err) {
    console.error("Resend invite error:", err);
    Swal.fire("Error", "Failed to resend invite", "error");
  }
};

const removeMember = async (memberId) => {
  if (!authStore.user || !authStore.user.accessToken) {
    console.error("Cannot remove member: No authenticated user");
    Swal.fire("Error", "Authentication required", "error");
    return;
  }

  try {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to remove this member from the team!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#0D423D",
      confirmButtonText: "Yes, remove them!"
    });

    if (result.isConfirmed) {
      const res = await fetch(`${global.API_URL}/users`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: memberId }),
      });
            
      // Clone the response and log the text content for debugging
      const responseClone = res.clone();
      const textResponse = await responseClone.text();
      
      // Try to parse JSON response
      let data;
      try {
        data = JSON.parse(textResponse);
      } catch (parseError) {
        console.error("Error parsing JSON remove response:", parseError);
        // Check if request was successful by status code
        if (res.ok) {
          Swal.fire("Removed!", "Member has been removed from the team.", "success");
          fetchTeamMembers(); // Refresh the team members list
          return;
        } else {
          throw new Error("Invalid response format from server");
        }
      }
      
      // Check for success using either 'status' or 'success' property
      if (data.success === true || data.status === true) {
        Swal.fire("Removed!", "Member has been removed from the team.", "success");
        fetchTeamMembers(); // Refresh the team members list
      } else {
        Swal.fire("Error", data.message || "Failed to remove member", "error");
      }
    }
  } catch (err) {
    console.error("Remove member error:", err);
    Swal.fire("Error", "Failed to remove member", "error");
  }
};

const resetError = () => {
  if (showEmailError.value) {
    showEmailError.value = false;
  }
};

// Utility methods
const getInitials = (member) => {
  // Priority 1: Use first and last name
  if (member.first_name && member.last_name) {
    return `${member.first_name[0]}${member.last_name[0]}`.toUpperCase();
  }

  // Priority 2: If we don't have first_name and last_name, use email
  if (member.email) {
    const emailPart = member.email.split('@')[0];
    
    // Try to extract initials from email - check if email contains dots, underscores, or hyphens
    // that might separate first and last name
    const separators = ['.', '_', '-'];
    for (const separator of separators) {
      if (emailPart.includes(separator)) {
        const parts = emailPart.split(separator);
        if (parts.length >= 2 && parts[0].length > 0 && parts[1].length > 0) {
          return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
      }
    }
    
    // If we couldn't extract initials in a first name/last name format,
    // simply use the first two characters of the email
    return emailPart.substring(0, 2).toUpperCase();
  }
  
  // Fallback if no email or name is available
  return '??';
};
</script>

<style scoped>

.shakeX {
  animation: shakeX 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  transform: translate3d(0, 0, 0);
}

@keyframes shakeX {
  10%, 90% {
    transform: translate3d(-1px, 0, 0);
  }
  20%, 80% {
    transform: translate3d(2px, 0, 0);
  }
  30%, 50%, 70% {
    transform: translate3d(-4px, 0, 0);
  }
  40%, 60% {
    transform: translate3d(4px, 0, 0);
  }
  70% { 
    transform: translate3d(-4px, 0, 0); 
  } 
  40%, 60% { 
    transform: translate3d(4px, 0, 0);
  } 
}

.auth-form-enter-active,
.auth-form-leave-active {
  transition: opacity 0.3s ease;
}

.auth-form-enter-from,
.auth-form-leave-to {
  opacity: 0;
}

.button-switch-enter-active,
.button-switch-leave-active {
  transition: opacity 0.15s;
}

.button-switch-enter-from,
.button-switch-leave-to {
  opacity: 0;
}

/* These are for the primary colors if not defined globally */
:root {
  --primary-300: #009C87;
  --primary-400: #00423D;
}

.bg-primary-300 {
  background-color: var(--primary-300);
}

.text-primary-300 {
  color: var(--primary-300);
}

.text-primary-400 {
  color: var(--primary-400);
}

.border-primary-300 {
  border-color: var(--primary-300);
}

.text-placeholder {
  color: #6B7280;
}

.fill-placeholder {
  fill: #6B7280;
}
</style>